import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

export function TodosPage() {
  const { t, i18n } = useTranslation();
  const [todos, setTodos] = useState([]);

  useEffect(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    const file = lang === "pl" ? "todos.pl.json" : "todos.en.json";
    import(/* webpackChunkName: "todos" */ `./${file}`)
      .then((mod) => {
        setTodos(mod.default || []);
      })
      .catch(() => {
        setTodos([]);
      });
  }, [i18n.language, i18n.resolvedLanguage]);

  const handleDownloadPdf = async () => {
    try {
      const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
      const res = await fetch(`${API_BASE_URL}/todos/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: lang }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "wedding-todos.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // fail silently for now
    }
  };

  // Group todos by section for display
  const grouped = todos.reduce((acc, item) => {
    const key = item.section || "";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const sections = Object.entries(grouped);

  return (
    <div className="min-h-[70vh] flex flex-col gap-8 py-12 text-slate-800">
      <section className="max-w-2xl space-y-4">
        <p className="text-sm uppercase tracking-[0.2em] text-slate-400">
          {t("todos.badge", "Checklist")}
        </p>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-slate-900">
          {t("todos.title", "Wedding to-do list")}
        </h2>
        <p className="text-base md:text-lg text-slate-600 max-w-xl">
          {t(
            "todos.description",
            "A structured checklist of key tasks before the wedding. Mark things as done and download a clean PDF to share or print.",
          )}
        </p>
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-900 bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white hover:bg-slate-800"
        >
          {t("todos.downloadPdf", "Download checklist as PDF")}
        </button>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        {sections.map(([sectionTitle, items]) => (
          <div
            key={sectionTitle}
            className="rounded-2xl border border-slate-200/80 bg-white/80 p-5 shadow-sm"
          >
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {sectionTitle}
            </h3>
            <ul className="space-y-2 text-sm text-slate-700">
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1 h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  />
                  <div>
                    <div className="font-medium text-slate-800">
                      {item.label}
                    </div>
                    {item.hint && (
                      <p className="text-xs text-slate-500">{item.hint}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  );
}
